import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DateInput from './DateInput';
import { getMainAxios } from '../utils/util';
import ResearchDocViewer from './ResearchDocViewer';
import {
  page, card, cardGrid, titleRow, gridTitle, gridHint, toolbarLabel,
  btnPrimary, selectSm, emptyMsg, listRow, listRowHeader, listRowDetail,
  colors,
} from '../styles/fdsTheme';

interface ListRes {
  kind: string;
  dates: string[];
}

interface DocRes {
  id: string;
  content: string;
  updatedUtc?: string;
}

interface Props {
  kind: 'market' | 'algo';
  title: string;
  subtitle: string;
}

const DEFAULT_DAYS = 10;

/** MD 한 줄 요약 — 목록 미리보기 */
function briefingSummary(content: string, kind: 'market' | 'algo'): { text: string; ok: boolean } {
  const failed = /query failed|\(query failed\)|\| — \| — \|/i.test(content);
  const ops = content.match(/\| ops 판정[^|]*\|\s*([^|]+)\|/i)?.[1]?.trim();
  const target = content.match(/분석 기준일 \*\*(\d{4}-\d{2}-\d{2})\*\*/)?.[1];
  const idxLine = content.match(/\| 0001 \|[^\n]+/);
  const orders = content.match(/\| 2 \| (\d+)/);

  if (kind === 'market') {
    const parts: string[] = [];
    if (target) parts.push(`기준일 ${target}`);
    if (idxLine && !idxLine[0].includes('—')) parts.push('지수 OK');
    else if (!failed) parts.push('지수·ETF');
    else parts.push('수집 실패 — daily_research 재실행');
    if (ops) parts.push(`ops ${ops}`);
    const narr = content.includes('- [x]') || /## 시황 서술[\s\S]*?(?=- \[ \]|$)/m.test(content.replace(/- \[ \]/g, ''));
    if (content.match(/코스피|코스닥|069500/i)) parts.push('서술 있음');
    else if (!narr) parts.push('서술 대기');
    return { text: parts.join(' · '), ok: !failed };
  }

  const parts: string[] = [];
  if (target) parts.push(`기준일 ${target}`);
  if (orders) parts.push(`intraday ${orders[1]}건`);
  if (failed) parts.push('집계 실패');
  return { text: parts.join(' · '), ok: !failed };
}

export default function ResearchBriefing({ kind, title, subtitle }: Props) {
  const [allDates, setAllDates] = useState<string[]>([]);
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [endD, setEndD] = useState(new Date().toISOString().split('T')[0]);
  const [openD, setOpenD] = useState<string | null>(null);
  const [docCache, setDocCache] = useState<Record<string, DocRes>>({});
  const [summaries, setSummaries] = useState<Record<string, { text: string; ok: boolean }>>({});
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dates = useMemo(
    () => allDates.filter(d => d <= endD).slice(0, days),
    [allDates, endD, days],
  );

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    setOpenD(null);
    try {
      const axios = getMainAxios();
      const res = await axios.get<ListRes>(`/api/v1/research/briefings/${kind}`);
      const list = res.data?.dates ?? [];
      setAllDates(list);

      // 목록 미리보기 — 상위 N일 본문 앞 2KB만 요약용
      const previewDates = list.filter(d => d <= endD).slice(0, days);
      const nextSummaries: Record<string, { text: string; ok: boolean }> = {};
      await Promise.all(
        previewDates.map(async d => {
          try {
            const dr = await axios.get<DocRes>(`/api/v1/research/briefings/${kind}/${d}`);
            nextSummaries[d] = briefingSummary(dr.data.content, kind);
          } catch {
            nextSummaries[d] = { text: '파일 없음', ok: false };
          }
        }),
      );
      setSummaries(nextSummaries);
    } catch (e: unknown) {
      setError(String(e));
      setAllDates([]);
    } finally {
      setLoadingList(false);
    }
  }, [kind, endD, days]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const loadDoc = useCallback(async (date: string) => {
    if (docCache[date]) return;
    setLoadingDoc(date);
    setError(null);
    try {
      const axios = getMainAxios();
      const res = await axios.get<DocRes>(`/api/v1/research/briefings/${kind}/${date}`);
      setDocCache(prev => ({ ...prev, [date]: res.data }));
      setSummaries(prev => ({ ...prev, [date]: briefingSummary(res.data.content, kind) }));
    } catch (e: unknown) {
      setError(String(e));
    } finally {
      setLoadingDoc(null);
    }
  }, [kind, docCache]);

  const toggle = async (d: string) => {
    if (openD === d) {
      setOpenD(null);
      return;
    }
    setOpenD(d);
    await loadDoc(d);
  };

  return (
    <div style={page}>
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span style={toolbarLabel}>
          {title} {loadingList ? '(조회 중...)' : `— 최근 ${dates.length}일`}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={days} onChange={e => setDays(Number(e.target.value))} style={selectSm}>
            {[10, 20, 30, 60].map(n => <option key={n} value={n}>최근 {n}일</option>)}
          </select>
          <DateInput value={endD} onChange={setEndD} style={{ fontSize: '12px' }} />
          <button type="button" onClick={fetchList} style={btnPrimary}>조회</button>
        </div>
      </div>

      <div style={cardGrid}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            {kind === 'market' ? '일별 시황 목록' : '알고리매매 목록'}
            <span style={gridHint}>(클릭: 상세 MD)</span>
          </h3>
          <span style={{ color: colors.title, fontSize: '12px', fontWeight: 500 }}>
            총 {dates.length}건
          </span>
        </div>
        <p style={{ ...emptyMsg, marginTop: 0, marginBottom: 12 }}>{subtitle}</p>

        {error && <p style={{ color: colors.up, fontSize: '12px' }}>{error}</p>}

        {!loadingList && dates.length === 0 && (
          <p style={emptyMsg}>briefings/{kind}/ 에 MD 없음 — daily_research.sh 실행 후 배포</p>
        )}

        {dates.map(d => {
          const open = openD === d;
          const sum = summaries[d];
          const accent = sum?.ok ? colors.btnPrimary : colors.divider;
          return (
            <div key={d} style={listRow(accent)}>
              <div style={listRowHeader} onClick={() => toggle(d)} role="button" tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter') toggle(d); }}>
                <span style={{ color: colors.hint, width: 12 }}>{open ? '▾' : '▸'}</span>
                <b style={{ color: colors.title, width: 88 }}>{d}</b>
                <span style={{ color: colors.body, flex: 1, minWidth: 200 }}>
                  {sum?.text ?? (loadingList ? '…' : '—')}
                </span>
                {loadingDoc === d && <span style={{ color: colors.hint, fontSize: '11px' }}>로딩…</span>}
              </div>
              {open && docCache[d]?.content && (
                <div style={listRowDetail}>
                  <ResearchDocViewer content={docCache[d].content} />
                </div>
              )}
              {open && !docCache[d] && loadingDoc !== d && (
                <p style={{ ...emptyMsg, paddingLeft: 22 }}>briefings/{kind}/{d}.md 없음</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
